/**
 * The Dashboard's header: the greeting, the briefing as one line with "Read
 * the Briefing" on the left, the view switch in the middle, and last updated,
 * Mine / Team and Display on the right.
 */
import { Sparkles } from "lucide-react";
import type { FC, ReactNode } from "react";

import { Button } from "@/components/ui/button";

import type { HomeScope, HomeView } from "../dashboardLayoutStore";
import { HomeDisplayMenu } from "./HomeDisplayMenu";
import { HomeHeaderRow } from "./HomeHeaderRow";
import { HomeScopeTabs, HomeViewTabs } from "./HomeViewBar";

type Props = {
  greeting: string;
  lead: string;
  onReadBriefing: () => void;
  view: HomeView;
  onViewChange: (view: HomeView) => void;
  scope: HomeScope;
  onScopeChange: (scope: HomeScope) => void;
  /** Start of the right group (last updated and refresh). */
  children?: ReactNode;
};

export const HomeGreetingHeader: FC<Props> = ({
  greeting,
  lead,
  onReadBriefing,
  view,
  onViewChange,
  scope,
  onScopeChange,
  children
}) => (
  <header className="shrink-0 px-6 pt-4.5" data-testid="home-greeting-header">
    <HomeHeaderRow
      testId="home-header-row"
      start={
        <div className="flex min-w-0 flex-col gap-0.5">
          <h1 className="truncate text-xl font-medium text-fg">{greeting}</h1>
          <p className="flex min-w-0 items-center gap-1.5 text-base text-fg-3">
            <Sparkles className="size-3.25 shrink-0" aria-hidden />
            <span className="truncate">{lead}</span>
            <Button
              variant="link"
              size="xs"
              className="shrink-0 text-fg underline decoration-line-4 underline-offset-3"
              onClick={onReadBriefing}
              data-testid="home-read-briefing"
            >
              Read the Briefing
            </Button>
          </p>
        </div>
      }
      center={<HomeViewTabs view={view} onViewChange={onViewChange} />}
      end={
        <>
          {children}
          <HomeScopeTabs scope={scope} onScopeChange={onScopeChange} />
          <HomeDisplayMenu view={view} variant="ghost" />
        </>
      }
    />
  </header>
);
